import type { TranscriptionSegment } from '../api/transcriptions';
import type { TranslationDisplayMode } from '../components/TranslationSettings';

/**
 * Represents a single search match within a segment
 */
export interface SearchMatch {
  segmentId: number;
  textType: 'original' | 'translation';
  startOffset: number;
  length: number;
}

/**
 * Escape special regex characters in a string
 */
export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Find all matches of a search query within segments based on display mode
 */
export function findAllMatches(
  segments: TranscriptionSegment[],
  query: string,
  displayMode: TranslationDisplayMode
): SearchMatch[] {
  if (!query || query.trim() === '') {
    return [];
  }

  const matches: SearchMatch[] = [];
  const escapedQuery = escapeRegExp(query);
  const regex = new RegExp(escapedQuery, 'gi');

  for (const segment of segments) {
    // Search original text based on display mode
    const searchOriginal = displayMode === 'original' || displayMode === 'bilingual';
    const searchTranslation = (displayMode === 'translation' || displayMode === 'bilingual')
      && segment.translation
      && segment.translation.trim() !== '';

    if (searchOriginal && segment.text) {
      let match: RegExpExecArray | null;
      while ((match = regex.exec(segment.text)) !== null) {
        matches.push({
          segmentId: segment.id,
          textType: 'original',
          startOffset: match.index,
          length: match[0].length,
        });
      }
      regex.lastIndex = 0; // Reset regex for next use
    }

    if (searchTranslation && segment.translation) {
      let match: RegExpExecArray | null;
      while ((match = regex.exec(segment.translation)) !== null) {
        matches.push({
          segmentId: segment.id,
          textType: 'translation',
          startOffset: match.index,
          length: match[0].length,
        });
      }
      regex.lastIndex = 0; // Reset regex for next use
    }
  }

  return matches;
}

/**
 * Group matches by segment id for efficient lookup during rendering.
 */
export function groupMatchesBySegmentId(matches: SearchMatch[]): Map<number, SearchMatch[]> {
  const grouped = new Map<number, SearchMatch[]>();
  for (const match of matches) {
    const existing = grouped.get(match.segmentId);
    if (existing) {
      existing.push(match);
    } else {
      grouped.set(match.segmentId, [match]);
    }
  }
  return grouped;
}

/**
 * Get the segment ID of a match at a given index
 */
export function getMatchSegmentId(matches: SearchMatch[], index: number): number | null {
  if (index < 0 || index >= matches.length) {
    return null;
  }
  return matches[index].segmentId;
}

/**
 * Check if a segment contains the current match
 */
export function isCurrentMatchSegment(
  segmentId: number,
  matches: SearchMatch[],
  currentMatchIndex: number
): boolean {
  if (currentMatchIndex < 0 || currentMatchIndex >= matches.length) {
    return false;
  }
  return matches[currentMatchIndex].segmentId === segmentId;
}

/**
 * Get matches for a specific segment
 */
export function getSegmentMatches(
  segmentId: number,
  matches: SearchMatch[]
): SearchMatch[] {
  return matches.filter(match => match.segmentId === segmentId);
}

/**
 * Get the current match if it belongs to a specific segment
 */
export function getCurrentMatchForSegment(
  segmentId: number,
  matches: SearchMatch[],
  currentMatchIndex: number
): SearchMatch | null {
  if (currentMatchIndex < 0 || currentMatchIndex >= matches.length) {
    return null;
  }
  const currentMatch = matches[currentMatchIndex];
  if (currentMatch.segmentId === segmentId) {
    return currentMatch;
  }
  return null;
}
